// Static content for all legal / about / contact pages.
// Kept as data (not JSX) so the same shell (`Legal.jsx`) can render every page
// and we get a consistent editorial voice without duplicating layout code.
//
// If you rewrite any policy substantially, bump the `updated` date so users
// (and search engines) see the change surface.

export const LEGAL_PAGES = {
  about: {
    title: "About WhichUniversityAdvisor",
    updated: "February 2026",
    lede:
      "We're not another ranking. We answer the four questions families actually ask: Can I get in? Can we afford it? Will I enjoy it? Will it take me where I want to go?",
    sections: [
      {
        heading: "Why we exist",
        body: [
          "University comparison sites optimise for what's easy to publish — league tables, brochure prose, marketing quotes. That helps no one who is actually choosing.",
          "WhichUniversityAdvisor.com starts from the student. Predicted grades, budget, subject, country preferences. Then we walk through 150,000+ degree records across the UK, US, Germany, France, Netherlands and beyond and tell you where you have a realistic shot, at a price your family can absorb, on a course that leads somewhere.",
        ],
      },
      {
        heading: "How we're funded",
        body: [
          "Free forever for families. Our free product — Quick Check, Advanced Search, city guides, degree pages — will always be free.",
          "WUA+ is a paid tier for households who want proactive help: an AI counsellor (Anna) that watches deadlines and application windows, a verified Family Wallet, and human counsellors matched to your destination country.",
          "We do not accept payment from universities for placement or ranking. Universities can claim a school portal to correct factual errors, but no amount of money buys them a higher position or friendlier prose.",
        ],
      },
      {
        heading: "The editorial promise",
        body: [
          "We publish warnings alongside strengths. If a university has weak student satisfaction, poor housing, a difficult visa route or a shrinking cohort in your subject, we say so — on the same page as its glossy statistics.",
          "Every degree page cites the underlying HESA / IPEDS / Numbeo / Scorecard source and the date the figure was pulled. If we can't verify a number, we don't publish it.",
        ],
      },
    ],
  },

  privacy: {
    title: "Privacy Policy",
    updated: "February 2026",
    lede:
      "We collect the minimum we need to help you find a good-fit university, and we never sell your data. In plain English:",
    sections: [
      {
        heading: "What we collect",
        body: [
          "When you use WhichUniversityAdvisor.com without signing in, we store your Quick Check inputs and shortlist locally in your browser only. Nothing is sent to a server tied to you.",
          "When you sign in with Google, we receive your name, email address and profile picture from Google. We use these to save your profile, shortlist and Saved Searches so you can revisit them from any device.",
          "We use PostHog to record anonymous product analytics (which pages are visited, which buttons are clicked). This is aggregated and never linked to your identity for advertising.",
        ],
      },
      {
        heading: "What we do not do",
        list: [
          "We do not sell your personal data to universities, agents, employers or advertisers.",
          "We do not share your predicted grades, budget or shortlist with any third party without your explicit consent.",
          "We do not use dark patterns to lock you into a paid tier — WUA+ is optional and the free product is not degraded to push you to upgrade.",
        ],
        body: [],
      },
      {
        heading: "Your rights (GDPR / UK GDPR)",
        body: [
          "You can request a full export of your data, or delete your account entirely, by emailing privacy@whichuniversityadvisor.com. We respond within 30 days as required by law.",
          "If you're in the UK or EU, you can also lodge a complaint with the ICO or your local data protection authority if you feel we've handled your data improperly.",
        ],
      },
      {
        heading: "Data storage",
        body: [
          "Data is stored in encrypted MongoDB clusters hosted in the EU. Backups are encrypted at rest and retained for 30 days.",
          "We use Google Sign-In for authentication (OAuth 2.0). We never see or store your Google password.",
        ],
      },
    ],
  },

  terms: {
    title: "Terms of Service",
    updated: "February 2026",
    lede:
      "These are the terms you agree to when you use WhichUniversityAdvisor.com. We've tried to keep them short and readable.",
    sections: [
      {
        heading: "Using the service",
        body: [
          "You can browse WhichUniversityAdvisor.com without signing in. To save searches, build a shortlist across devices or contact a counsellor, you'll need to sign in with a Google account.",
          "You must be 13 or older to create an account. Under-16s in the UK and under-16s in some EU countries need parental consent — if you're unsure, ask a parent or guardian before signing in.",
        ],
      },
      {
        heading: "What we promise",
        body: [
          "We work hard to keep every number on the site accurate and cited. That said, university statistics change every cycle: fees rise, visa rules shift, courses close. We cannot guarantee any figure is current the moment you read it — always confirm on the university's own site before making a decision.",
          "Admission chance estimates are educated guides based on prior HESA/UCAS data and published entry standards. They are not offers. Only the university itself can make you an offer.",
        ],
      },
      {
        heading: "What we ask of you",
        list: [
          "Don't scrape the site with automated tools. If you need bulk data for research, email us — we're friendly.",
          "Don't try to game the Save-a-Search or Shortlist endpoints to hammer our servers. Rate limits apply.",
          "Don't misrepresent yourself as a university representative to claim a school portal — we verify claims.",
        ],
        body: [],
      },
      {
        heading: "Termination",
        body: [
          "You can delete your account at any time via the user menu → Delete account, or by emailing privacy@whichuniversityadvisor.com. We'll remove your profile, saved searches, shortlist and email within 30 days.",
          "We may suspend accounts that violate these terms (e.g. automated scraping, harassment of counsellors). We'll email you first where possible.",
        ],
      },
      {
        heading: "Liability",
        body: [
          "WhichUniversityAdvisor.com provides guidance, not offers. Decisions about where to apply, what to spend and where to live are yours — we're not liable for outcomes that depend on those decisions.",
          "Nothing in these terms excludes our liability for anything that cannot lawfully be excluded (e.g. fraud, gross negligence, or your statutory rights as a consumer).",
        ],
      },
      {
        heading: "Governing law",
        body: [
          "These terms are governed by the laws of England and Wales. Any dispute will be resolved in the English courts, unless local consumer law in your country of residence gives you a stronger right.",
        ],
      },
    ],
  },

  cookies: {
    title: "Cookie Policy",
    updated: "February 2026",
    lede:
      "We use a small number of cookies to keep the site working and to understand which features families find useful.",
    sections: [
      {
        heading: "Essential cookies",
        body: [
          "These keep you signed in and remember your currency preference. They cannot be disabled without breaking the site.",
        ],
        list: [
          "session — set when you sign in via Google. Cleared when you sign out.",
          "wua:currency — remembers whether you want prices shown in GBP, USD, EUR, INR etc.",
        ],
      },
      {
        heading: "Analytics cookies",
        body: [
          "We use PostHog for anonymous product analytics — which pages families visit, which buttons they click. This helps us decide what to build next.",
          "You can opt out by emailing privacy@whichuniversityadvisor.com or by using your browser's Do Not Track setting, which we honour.",
        ],
      },
      {
        heading: "No advertising cookies",
        body: [
          "We do not run advertising cookies. We do not use Facebook, TikTok or Google Ads pixels. Your browsing on WhichUniversityAdvisor.com is not used to retarget you elsewhere.",
        ],
      },
    ],
  },

  accessibility: {
    title: "Accessibility Statement",
    updated: "February 2026",
    lede:
      "WhichUniversityAdvisor.com aims to be usable by every family, regardless of ability. Here's what we've committed to.",
    sections: [
      {
        heading: "Standards we follow",
        body: [
          "We target WCAG 2.1 Level AA. Every interactive element has a keyboard focus state, a screen-reader-accessible name and enough colour contrast against its background.",
          "Where we fall short, we say so on this page and give an ETA for the fix.",
        ],
      },
      {
        heading: "Known gaps",
        body: [
          "The interactive salary-comparison chart on employer pages is not yet fully screen-reader friendly. A tabular fallback is available below every chart, and a full screen-reader-first rewrite is on the Q2 2026 roadmap.",
          "Some third-party embeds (e.g. Numbeo iframes on city pages) are outside our control and may not meet AA. We are working with those providers.",
        ],
      },
      {
        heading: "Report a problem",
        body: [
          "If you hit a barrier we haven't listed, email accessibility@whichuniversityadvisor.com. We aim to reply within 5 working days and to fix or work around the issue within 30.",
        ],
      },
    ],
  },

  contact: {
    title: "Contact us",
    updated: "February 2026",
    lede:
      "Talk to a human. We read every message and reply within two working days.",
    sections: [
      {
        heading: "Families",
        body: [
          "General questions, feedback on a degree page, or a factual correction: hello@whichuniversityadvisor.com",
          "Book a 1-1 with a WUA+ counsellor via the Specialists page.",
        ],
      },
      {
        heading: "Universities",
        body: [
          "To claim your school portal (edit your entry, respond to reviews, correct factual errors): claims@whichuniversityadvisor.com",
          "We do not accept paid placement or paid rankings. Please don't ask.",
        ],
      },
      {
        heading: "Press & partnerships",
        body: [
          "Journalists, editors and partner platforms: press@whichuniversityadvisor.com",
        ],
      },
      {
        heading: "Privacy, data & legal",
        body: [
          "Data access requests, deletion requests, or anything under GDPR: privacy@whichuniversityadvisor.com",
        ],
      },
      {
        heading: "Postal address",
        body: [
          "WhichUniversityAdvisor Ltd, 128 City Road, London EC1V 2NX, United Kingdom.",
        ],
      },
    ],
  },
};

// Ordered list used to render the "Related" footer on every legal page,
// and to build the /legal index dropdown in the site footer.
export const LEGAL_INDEX = [
  { slug: "about", title: "About us" },
  { slug: "contact", title: "Contact" },
  { slug: "privacy", title: "Privacy Policy" },
  { slug: "terms", title: "Terms of Service" },
  { slug: "cookies", title: "Cookie Policy" },
  { slug: "accessibility", title: "Accessibility" },
];
